
import { memo } from 'react';

const ServiceCard = memo(({ service, index }: { service: any; index: number }) => (
  <div className="bg-white border border-gray-100 p-4 sm:p-6 md:p-8 lg:p-10 space-y-5 sm:space-y-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
    <div className="flex items-center gap-3 sm:gap-4">
      <div className="w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 flex items-center justify-center bg-[#6b8e6b]/10 rounded-sm flex-shrink-0">
        <i className={`${service.icon} text-lg sm:text-xl md:text-2xl text-[#6b8e6b]`}></i>
      </div>
      <h3 className="text-[0.9rem] sm:text-base md:text-lg text-[#333333] tracking-wide font-normal">{service.title}</h3>
    </div>

    <div className="space-y-4 sm:space-y-5">
      <div>
        <h4 className="text-[0.8rem] sm:text-sm md:text-base text-[#333333] mb-2.5 sm:mb-3 tracking-wide font-normal">こんな課題を解決</h4>
        <ul className="space-y-2">
          {service.problems.map((problem: string, pIndex: number) => (
            <li key={pIndex} className="flex items-start gap-2">
              <i className="ri-close-circle-line text-[#c9a87c] mt-0.5 flex-shrink-0 text-xs sm:text-sm"></i>
              <span className="text-[0.75rem] sm:text-sm text-[#555555] leading-[1.7] font-light">{problem}</span>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="text-[0.8rem] sm:text-sm md:text-base text-[#333333] mb-2.5 sm:mb-3 tracking-wide font-normal">Naturiの支援内容</h4>
        <ul className="space-y-2">
          {service.support.map((item: string, sIndex: number) => (
            <li key={sIndex} className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[#6b8e6b] mt-0.5 flex-shrink-0 text-xs sm:text-sm"></i>
              <span className="text-[0.75rem] sm:text-sm text-[#555555] leading-[1.7] font-light">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="text-[0.8rem] sm:text-sm md:text-base text-[#333333] mb-2.5 sm:mb-3 tracking-wide font-normal">成果イメージ</h4>
        <ul className="space-y-2">
          {service.results.map((result: string, rIndex: number) => (
            <li key={rIndex} className="flex items-start gap-2">
              <i className="ri-check-line text-[#6b8e6b] mt-0.5 flex-shrink-0 text-xs sm:text-sm"></i>
              <span className="text-[0.75rem] sm:text-sm text-[#555555] leading-[1.7] font-light">{result}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
));

ServiceCard.displayName = 'ServiceCard';

export default function ServicesSection() {
  const services = [
    {
      icon: 'ri-settings-3-line',
      title: 'DX戦略設計/実行支援',
      problems: [
        'DXを進めたいが、何から始めればいいか分からない',
        'ITツールを導入したが、業務に活かせていない',
        '社内に詳しい人材がおらず、判断できない',
      ],
      support: [
        '現状の業務フローを分析し、課題を可視化',
        '貴社に最適なITツール・システムの選定',
        '導入から運用定着まで、実務レベルで支援',
        '費用対効果を重視した経営視点でのアドバイス',
      ],
      results: [
        '業務効率化により、社員が本来の業務に集中できる',
        'データ活用により、経営判断のスピードが上がる',
        '属人化を解消し、組織として持続可能な体制を構築',
      ],
    },
    {
      icon: 'ri-shopping-cart-line',
      title: 'ECモール/SNS運用支援',
      problems: [
        '楽天市場やYahoo!ショッピングを始めたいが、運用方法が分からない',
        'SNS(Instagram/TikTok)で集客したいが、何を投稿すればいいか分からない',
        '運用代行に頼んでいるが、成果が不明',
      ],
      support: [
        '楽天市場/Yahoo!ショッピングの出店から運用最適化',
        '商品ページの改善(SEO対策、写真・テキスト最適化)',
        'Instagram/TikTokの投稿戦略設計と運用代行',
        'SNSからECサイトへの導線設計',
        '月次レポートと改善PDCAの実施',
      ],
      results: [
        'ECモール内での検索順位が上がり、売上が増加',
        'SNSのフォロワーが増え、認知度が向上',
        'SNSからECサイトへの流入が増加し、売上に直結',
      ],
    },
    {
      icon: 'ri-team-line',
      title: '採用/広報支援(SNS活用)',
      problems: [
        '若手人材の採用に苦戦している',
        '求人広告を出しても、応募が集まらない',
        '自社の魅力をどう伝えればいいか分からない',
      ],
      support: [
        'Instagram等を活用した採用/広報戦略の設計',
        '企業の魅力を可視化するコンテンツ制作',
        '投稿代行および効果測定',
        '採用コストを抑えながら、自社に合う人材を惹きつける仕組み作り',
      ],
      results: [
        'SNS経由での応募が増加',
        '企業の認知度が上がり、ブランディングにも寄与',
        '採用コストを削減しながら、質の高い人材を獲得',
      ],
    },
    {
      icon: 'ri-book-open-line',
      title: 'e-learning研修',
      problems: [
        '社員のITリテラシーを向上させたい',
        '外部に頼らず、社内で運用できる体制を作りたい',
      ],
      support: [
        'ECモール運用の基礎',
        'SNSマーケティングの実践',
        'ITツールの使い方研修',
        'データ分析とレポーティング',
      ],
      results: [
        '社員が自分で判断し、実行できるようになる',
        '外部依存から脱却し、社内にノウハウが蓄積される',
      ],
    },
  ];

  return (
    <section id="services" className="py-16 sm:py-24 md:py-32 lg:py-36 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-16 xl:px-24">
        <div className="text-center mb-10 sm:mb-14 md:mb-18">
          <h2 className="text-lg sm:text-xl md:text-2xl text-[#333333] tracking-wider">
            サービス内容
          </h2>
        </div>

        <div className="max-w-4xl mx-auto space-y-6 sm:space-y-8 md:space-y-10">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
